<script setup lang="ts">
import { Combobox, Listbox } from '@headlessui/vue'

export type Option = {
    label: string
    value?: any
    group?: string | number
    groupLabel?: string
    disabled?: boolean
    padding?: number
}

const props = withDefaults(
    defineProps<{
        modelValue?: any
        options?: Option[]
        label?: string
        multiple?: boolean
        nullable?: boolean
        direction?: 'left' | 'right' | 'top' | 'bottom'
        selectable?: boolean
        autocomplete?: boolean
        noSelectionText?: string
        selectionText?: string
        selectAllText?: string
        selectAllValue?: any
        small?: boolean
        large?: boolean
        valueKey?: string
        labelKey?: string
        groupKey?: string
        groupLabel?: string
        disabledKey?: string
    }>(),
    {
        noSelectionText: '-',
        selectionText: '{value} items selected',
        direction: 'bottom',
        selectable: false,
        nullable: false,
        options: undefined,
        modelValue: undefined,
        label: undefined,
        selectAllText: undefined,
        selectAllValue: undefined,
        valueKey: 'value',
        labelKey: 'label',
        groupKey: 'group',
        disabledKey: 'disabled',
        groupLabel: undefined
    }
)

const emit = defineEmits<{
    (_e: 'update:modelValue', _value?: any): void
}>()

const value = computed({
    get() {
        return props.modelValue ?? (props.multiple ? [] : undefined)
    },
    set(value) {
        emit('update:modelValue', value)
    }
})

function getOptionValue(option: Option) {
    return option[props.valueKey as keyof Option]
}

function getOptionLabel(option: Option) {
    return option[props.labelKey as keyof Option]
}

function getOptionGroup(option: Option) {
    return option[props.groupKey as keyof Option]
}

function getOptionGroupLabel(option: Option) {
    return option[props.groupLabel as keyof Option]
}

function getOptionDisabled(option: Option): boolean {
    return option[props.disabledKey as keyof Option]
}

const normalizedOptions = computed(() => {
    return props.options?.map(option => ({
        value: getOptionValue(option),
        label: getOptionLabel(option),
        group: getOptionGroup(option),
        groupLabel: getOptionGroupLabel(option),
        disabled: getOptionDisabled(option),
        padding: option.padding ?? 0
    }))
})
const selected = computed(() => {
    if (props.multiple) {
        return unref(normalizedOptions)?.filter(
            option =>
                props.modelValue?.includes(option.value) && !option.disabled
        )
    }

    return unref(normalizedOptions)?.find(
        option => option.value === props.modelValue && !option.disabled
    )
})

const query = ref()

function clear() {
    if (props.multiple) {
        value.value = []
    } else {
        value.value = null
    }

    query.value = ''
}
</script>
    
<template>
    <component
        :is="autocomplete ? Combobox : Listbox"
        v-model="value"
        :nullable="nullable"
        :multiple="multiple"
    >
        <div class="flex flex-col">
            <UiSelectLabel :autocomplete="autocomplete" :label="label" />

            <div class="relative">
                <slot name="activator">
                    <UiSelectButton
                        v-model:query="query"
                        :autocomplete="autocomplete"
                        :options="normalizedOptions"
                        :selected="selected"
                        :no-selection-text="noSelectionText"
                        :selection-text="selectionText"
                        :multiple="multiple"
                        :small="small"
                        :large="large"
                        @clear="clear"
                    />
                </slot>

                <Transition
                    leave-active-class="transition duration-100 ease-in"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
                >
                    <UiSelectOptions
                        v-model="value"
                        :autocomplete="autocomplete"
                        :options="normalizedOptions"
                        :direction="direction"
                        :selectable="selectable"
                        :query="query"
                        :nullable="nullable"
                        :multiple="multiple"
                        :select-all-text="selectAllText"
                        :select-all-value="selectAllValue"
                    >
                        <slot />
                    </UiSelectOptions>
                </Transition>
            </div>
        </div>
    </component>
</template>